/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import Switch from '@ion/dls/src/components/switch/Switch';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {ViewSubGroupsPosition} from '@ion/dls/src/types/types';

export const useStyles = makeStyles(() => ({
  container : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  labelGridCtas : {
    ...dlsTypography('h4'),
    color         : dlsColor('grey2'),
    letterSpacing : '1px',
    marginRight   : '15px'
  },
  labelGridPaginationControls : {
  }
}));

export interface ViewSubGroupsSwitchProps {
  checked: boolean;
  onChange: Function;
  disabled?: boolean;
  position?: ViewSubGroupsPosition;
  label?: string;
};

/**
 * Defines the OnlyThisAccountSwitch component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * <OnlyThisAccountSwitch checked={viewSubGroups}
 *   onChange={toggleSubGroupsView}
 *   position={ViewSubGroupsPosition.GRID_CTAS} />
 *
 * ```
 */
export const OnlyThisAccountSwitch = ({
  checked,
  onChange,
  disabled = false,
  position = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS,
  label = translate('devicefiles:thisAccountToggle')
}: ViewSubGroupsSwitchProps) => {
  const classes     = useStyles();
  const isMounted   = useHasMounted();
  const {isDesktop} = useMedia();

  const labelParams = useMemo(() => ({
    [ViewSubGroupsPosition.GRID_CTAS] : {
      offset : '-8px',
      class  : classes.labelGridCtas
    },
    [ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS] : {
      offset : '-0px',
      class  : classes.labelGridPaginationControls
    }
  }), [classes]);

  return (
    <div className={classes.container}>
      <div>
        <Switch checked={checked}
          disabled={!isMounted || disabled}
          marginRight={labelParams[position]?.offset}
          onChange={onChange} />
      </div>
      {isDesktop && (
        <div className={labelParams[position]?.class}>
          {label}
        </div>
      )}
    </div>
  );
};

export default memo(OnlyThisAccountSwitch);
